
import React, { useState, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useDolphin } from '../../hooks/useDolphin';
//import { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS } from '../../config';
import DolphinPaymentsABI from '../../abis/DolphinPayments.json';
import { FaUserPlus, FaQrcode, FaShareAlt, FaDollarSign, FaChartLine, FaLevelUpAlt } from 'react-icons/fa';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import QRCode from 'react-qr-code';

// Prevent mobile zoom
if (typeof window !== 'undefined') {
  const meta = document.createElement('meta');
  meta.name = 'viewport';
  meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
  document.head.appendChild(meta);
}

// Helper: get direct downlines (level 1 only)
// Removed unused function: getDirectDownlines



const CopyButton = ({ text, type, copied, onCopy, disabled = false }) => (
  <button
    onClick={() => onCopy(type, text)}
    disabled={disabled}
    className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-cyan-700 hover:bg-cyan-800 text-white transition disabled:bg-gray-700 disabled:text-gray-400"
    style={{ minHeight: 0, minWidth: 0 }}
  >
    <HiOutlineClipboardCopy className="w-3 h-3" />
    {copied === type ? 'Copied!' : 'Copy'}
  </button>
);

// Removed unused component: Badge

// Earnings Calculator Component
function ReferralEarningsCalculator() {
  const { contract, account, userStats } = useDolphin();
  const [userLevels, setUserLevels] = useState([]);
  const [potentialEarnings, setPotentialEarnings] = useState({});
  const [loading, setLoading] = useState(true);
  const isSpecial = userStats?.isSpecial;
  const mlmPercents = React.useMemo(() => [40, 4, 3, 2, 1], []);

  useEffect(() => {
    const calculateEarnings = async () => {
      setLoading(true);
      try {
        let ownedLevels = [];
        if (isSpecial) {
          ownedLevels = Array.from({ length: 12 }, (_, i) => i + 1);
        } else if (contract && account) {
          const levelChecks = await Promise.all(
            Array.from({ length: 12 }, (_, i) => contract.hasLevel(account, i + 1))
          );
          ownedLevels = levelChecks
            .map((owned, idx) => (owned ? idx + 1 : null))
            .filter(Boolean);
        }
        setUserLevels(ownedLevels);
        
        const earnings = {};
        for (let level = 1; level <= mlmPercents.length; level++) {
          let canEarn = true;
          let missingLevel = null;
          for (let l = 1; l <= level; l++) {
            if (!ownedLevels.includes(l)) {
              canEarn = false;
              if (missingLevel === null) missingLevel = l;
            }
          }
          const percentage = mlmPercents[level - 1];
          earnings[level] = {
            canEarn: isSpecial ? true : canEarn,
            percentage,
            requiredLevel: level,
            message: isSpecial || canEarn
              ? `Lvl ${level}`
              : `Need Lvl ${missingLevel}`
          };
        }
        setPotentialEarnings(earnings);
      } catch (error) {
        console.error('Error calculating earnings:', error);
      } finally {
        setLoading(false);
      }
    };
    calculateEarnings();
  }, [contract, account, isSpecial, mlmPercents]);

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 border border-gray-700 shadow-xl w-full">
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="p-1.5 sm:p-2 md:p-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg sm:rounded-xl">
          <FaChartLine className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-white truncate">Earnings Potential</h3>
          <p className="text-xs sm:text-sm text-gray-400 truncate">Based on your levels</p>
        </div>
      </div>

      {/* Current Levels */}
      <div className="mb-4 sm:mb-6">
        <p className="text-xs sm:text-sm text-gray-400 mb-2 truncate">Your Active Levels</p>
        {loading ? (
          <div className="animate-pulse flex gap-1 sm:gap-2">
            <div className="h-6 w-10 sm:h-8 sm:w-16 bg-gray-800 rounded-full"></div>
            <div className="h-6 w-12 sm:h-8 sm:w-20 bg-gray-800 rounded-full"></div>
          </div>
        ) : userLevels.length > 0 ? (
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {userLevels.slice(0, 4).map(level => (
              <span key={level} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${level <= 3 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                L{level}
              </span>
            ))}
            {userLevels.length > 4 && (
              <span className="text-xs text-gray-400">+{userLevels.length - 4} more</span>
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-xs sm:text-sm">No active levels</div>
        )}
      </div>

      {/* Earnings Breakdown */}
      <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
        {Object.entries(potentialEarnings).map(([level, info]) => (
          <div 
            key={level} 
            className={`
              relative overflow-hidden rounded-lg sm:rounded-xl p-2 sm:p-3 transition-all duration-300
              ${info.canEarn 
                ? 'bg-gradient-to-r from-emerald-900/20 to-emerald-900/10 border border-emerald-800/30' 
                : 'bg-gradient-to-r from-violet-900/20 to-violet-900/10 border border-violet-800/30'
              }
            `}
          >
            <div className="flex items-center justify-between gap-1 sm:gap-2">
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
                <div className={`
                  w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${info.canEarn 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-violet-500/20 text-violet-400'
                  }
                `}>
                  {info.canEarn ? (
                    <FaLevelUpAlt className="w-2 h-2 sm:w-3 sm:h-3" />
                  ) : (
                    <FaDollarSign className="w-2 h-2 sm:w-3 sm:h-3" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-white truncate">
                    {info.message}
                  </p>
                  <p className={`text-xs ${info.canEarn ? 'text-emerald-300' : 'text-violet-300'} truncate`}>
                    {info.canEarn ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`
                  text-sm sm:text-base md:text-lg font-bold
                  ${info.canEarn ? 'text-emerald-400' : 'text-violet-400'}
                `}>
                  {info.percentage}%
                </div>
                <div className="text-xs text-gray-400 hidden sm:block">Comm</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Main Referral Component
const Referral = () => {
  const { account } = useWallet();
  const {
    inviteCode,
    referrer,
    dolphinContract,
    generateInviteCode,
    getMyReferralLink,
    formatUSDT,
    referralStats: stats,
    // Removed unused variables: downlineTree, transactionHistory, historyLoading, userStats
  } = useDolphin();
  const [displayInvite, setDisplayInvite] = useState('');
  useEffect(() => {
    if (inviteCode) {
      try {
        const { ethers } = require('ethers');
        // If it's a hex string and 66 chars (bytes32), try to parse as ASCII
        if (typeof inviteCode === 'string' && inviteCode.startsWith('0x') && inviteCode.length === 66) {
          // Remove trailing zeros and decode
          let ascii = ethers.utils.parseBytes32String(inviteCode);
          setDisplayInvite(ascii);
        } else if (typeof inviteCode !== 'string') {
          // If it's a Uint8Array or Buffer, convert to hex then parse
          let hex = ethers.utils.hexlify(inviteCode);
          let ascii = ethers.utils.parseBytes32String(hex);
          setDisplayInvite(ascii);
        } else {
          setDisplayInvite(inviteCode);
        }
      } catch {
        setDisplayInvite(String(inviteCode));
      }
    } else {
      setDisplayInvite('');
    }
  }, [inviteCode]);
  



  // Level 1 referrals from contract (downlinesByRank[0])
  const directDownlines = Array.isArray(stats?.downlinesByRank) && Array.isArray(stats.downlinesByRank[0])
    ? stats.downlinesByRank[0].map(addr => ({ address: addr }))
    : [];
  // Check if user owns any level
  // Removed unused variable: ownsAnyLevel

  // ========== Referral Payout History ========== //
  const [referralPayouts, setReferralPayouts] = useState([]);
  const [referralPayoutsLoading, setReferralPayoutsLoading] = useState(false);

  useEffect(() => {
    async function fetchReferralPayouts() {
      if (!account || !window.ethereum || !CONTRACTS.DOLPHIN_PAYMENTS) return;
      setReferralPayoutsLoading(true);
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const contract = new ethers.Contract(CONTRACTS.DOLPHIN_PAYMENTS, DolphinPaymentsABI, provider);
        const filter = contract.filters.ReferralPayout(null, account);
        const events = await contract.queryFilter(filter, 0, 'latest');
        const payouts = events.map(e => ({
          from: e.args.from,
          to: e.args.to,
          amount: ethers.utils.formatUnits(e.args.amount, 6),
          tx: e.transactionHash,
          block: e.blockNumber
        })).reverse();
        setReferralPayouts(payouts);
      } catch (err) {
        setReferralPayouts([]);
      } finally {
        setReferralPayoutsLoading(false);
      }
    }
    fetchReferralPayouts();
  }, [account]);

  const [copied, setCopied] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [invite, setInvite] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState('');
  // Removed unused variables: totalSpent, statsLoading

  // Effects
  useEffect(() => {
    setInvite(displayInvite || '');
  }, [displayInvite]);

  useEffect(() => {
    // Removed references to setTotalSpent and setStatsLoading
  }, [dolphinContract, account, formatUSDT]);

  // Use the decoded invite code for the referral link if available
  const referralLink = displayInvite ? `${window.location.origin}/?ref=${displayInvite}` : getMyReferralLink();

  // Handlers
  const handleGenerateInviteCode = async () => {
    setGenLoading(true);
    setGenError('');
    try {
      // Always trigger wallet connect if not connected
      if (!account && window.ethereum) {
        await window.ethereum.request({ method: 'eth_requestAccounts' });
      }
      const result = await generateInviteCode();
      if (result === false) {
        setGenError('You must buy Level 1 before you can generate an invite code.');
        alert('You must buy Level 1 before you can generate an invite code.');
      }
    } catch (err) {
      setGenError(err.message || 'Failed to generate invite code');
    } finally {
      setGenLoading(false);
    }
  };

  const handleCopy = (type, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleShare = async () => {
    if (navigator.share && referralLink) {
      try {
        await navigator.share({
          title: 'Join Dolphin Protocol',
          text: 'Join me on Dolphin Protocol and start earning!',
          url: referralLink,
        });
      } catch (error) {
        console.log('Sharing cancelled or not supported');
      }
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white p-1 sm:p-2 md:p-3 lg:p-4 overflow-x-hidden">
      <div className="w-full flex flex-col gap-3 sm:gap-4 md:gap-6 lg:gap-8">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          <div className="p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg">
            <FaUserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold truncate">Referral Program</h1>
            <p className="text-xs sm:text-sm text-gray-400 truncate">Invite friends & earn</p>
          </div>
        </div>

        {/* Invite Code & Referral Link Row */}
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-3 border border-gray-700 shadow-xl flex-1">
            {/* Referred By */}
            {referrer && (
              <div className="mb-2 p-1 bg-gray-800/40 rounded border border-gray-700 flex items-center gap-1">
                <FaUserPlus className="w-2 h-2 text-emerald-500 flex-shrink-0" />
                <span className="text-[10px] text-gray-400">Referred By</span>
                <code className="text-[10px] font-mono text-violet-400 truncate">{referrer}</code>
              </div>
            )}
            {/* Invite Code */}
            <label className="block text-xs font-medium text-gray-300 mb-1 truncate">Your Invite Code</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={displayInvite || 'Generate code to start earning'}
                readOnly
                className="flex-1 bg-gray-800/60 border border-gray-700 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent truncate"
              />
              <CopyButton 
                text={displayInvite}
                type="code"
                copied={copied}
                onCopy={handleCopy}
                disabled={!displayInvite}
              />
              {!displayInvite && (
                <button
                  onClick={handleGenerateInviteCode}
                  disabled={genLoading}
                  className="px-3 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 rounded font-medium transition-all duration-200 active:scale-95 disabled:opacity-50 text-xs min-h-[32px]"
                >
                  {genLoading ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <span>Gen</span>
                  )}
                </button>
              )}
            </div>
            {/* Error Message */}
            {genError && (
              <div className="mt-2 p-2 bg-red-900/20 border border-red-800/50 rounded">
                <p className="text-xs text-red-400 text-center truncate">{genError}</p>
              </div>
            )}
          </div>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-3 border border-gray-700 shadow-xl flex-1">
            <label className="block text-xs font-medium text-gray-300 mb-1 truncate">Referral Link</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={referralLink || 'Waiting for invite code...'}
                readOnly
                className="flex-1 bg-gray-800/60 border border-gray-700 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent truncate"
              />
              <CopyButton 
                text={referralLink}
                type="link"
                copied={copied}
                onCopy={handleCopy}
                disabled={!invite}
              />
              <button
                onClick={handleShare}
                disabled={!invite}
                className="px-2 py-2 bg-gray-800 hover:bg-gray-700 rounded font-medium transition-all duration-200 active:scale-95 disabled:opacity-50 text-xs min-h-[32px]"
              >
                <FaShareAlt className="w-3 h-3" />
              </button>
              <button
                onClick={() => setShowQR(!showQR)}
                className="relative px-2 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors min-h-[32px] flex items-center justify-center"
              >
                <FaQrcode className="w-3 h-3 text-cyan-400" />
                {showQR && invite && (
                  <div className="absolute top-full right-0 mt-1 p-2 bg-white rounded shadow-2xl z-50">
                    <QRCode value={referralLink} size={80} className="w-20 h-20" />
                    <p className="text-xs text-gray-600 text-center font-medium truncate">Scan to join</p>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Direct Downlines & Referral Payouts Row */}
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-3 border border-gray-700 shadow-xl flex-1">
            <div className="flex items-center gap-2 mb-2">
              <FaUserPlus className="w-3 h-3 text-cyan-400" />
              <h3 className="text-xs font-bold truncate">Level 1 Referrals</h3>
              <span className="ml-auto text-xs text-gray-400">{directDownlines.length}</span>
            </div>
            {directDownlines.length === 0 ? (
              <div className="text-xs text-gray-400 text-center py-2">No direct referrals yet</div>
            ) : (
              <ul className="flex flex-col gap-2">
                {directDownlines.slice(0, 5).map((d, i) => (
                  <li key={d.address} className="flex items-center gap-2 bg-gray-800/40 rounded px-2 py-2">
                    <span className="w-4 h-4 flex items-center justify-center text-[10px] text-gray-400 bg-gray-900 rounded-full">{i + 1}</span>
                    <code className="text-xs font-mono text-gray-300 truncate max-w-[100px]">{d.address}</code>
                    <span className="ml-auto text-xs text-cyan-400">{d.joinDate ? new Date(d.joinDate).toLocaleDateString() : ''}</span>
                  </li>
                ))}
                {directDownlines.length > 5 && (
                  <li className="text-xs text-gray-400 text-center py-2">+ {directDownlines.length - 5} more</li>
                )}
              </ul>
            )}
          </div>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-3 border border-gray-700 shadow-xl flex-1">
            <div className="flex items-center gap-2 mb-2">
              <FaDollarSign className="w-3 h-3 text-emerald-400" />
              <h3 className="text-xs font-bold truncate">Referral Payouts</h3>
              <span className="ml-auto text-xs text-gray-400">{referralPayouts.length}</span>
            </div>
            {referralPayoutsLoading ? (
              <div className="flex items-center justify-center py-2">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-emerald-500 border-t-transparent mr-2"></div>
                <span className="text-xs text-gray-400">Loading payouts...</span>
              </div>
            ) : referralPayouts.length === 0 ? (
              <div className="text-xs text-gray-400 text-center py-2">No referral payouts yet</div>
            ) : (
              <ul className="flex flex-col gap-2">
                {referralPayouts.slice(0, 5).map((p, i) => (
                  <li key={p.tx + i} className="flex items-center gap-2 bg-gray-800/40 rounded px-2 py-2">
                    <code className="text-xs font-mono text-gray-300 truncate max-w-[80px]">{p.from}</code>
                    <span className="text-xs text-emerald-400 font-bold">{p.amount}</span>
                    <a href={`https://polygonscan.com/tx/${p.tx}`} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 underline ml-auto">View</a>
                  </li>
                ))}
                {referralPayouts.length > 5 && (
                  <li className="text-xs text-gray-400 text-center py-2">+ {referralPayouts.length - 5} more</li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { ReferralEarningsCalculator };
export default Referral;
